import React, { useEffect, useState } from "react";
import { FaHourglassHalf } from "react-icons/fa";
import { formatDateTimeNumeric as tzDateTimeNumeric } from "../../utils/datetime";

/**
 * Center-action SLA countdown for the physiotherapy order-management flow.
 *
 * Two visual variants (mirrors the lab app):
 *  - "badge"  (default): compact pill "3h 57m left" — used in order tables.
 *  - "detail":           order-details header pill "Time Remaining: 03:00:00 Hrs"
 *                        with an hourglass icon and a live HH:MM:SS countdown.
 *                        When breached it flips to "Overdue by HH:MM:SS" and keeps
 *                        counting up, so it's always visibly live.
 *
 * Props:
 *  - dueAt:    ISO string | Date  — deadline (center action OR patient confirmation)
 *  - isUrgent: boolean            — server flag set when the reminder fired
 *  - status:   string             — order status (to hide once acted on)
 *  - variant:  "badge" | "detail" — visual style (default "badge")
 *  - visibleStatuses: string[]    — statuses in which the timer should render.
 *                                   Defaults to the center-action waiting set.
 *                                   Pass null to always render.
 */
const CENTER_WAITING_STATUSES = ["REQUEST_SUBMITTED", "AWAITING_CONFIRMATION"];

const formatRemaining = (ms) => {
  const totalMinutes = Math.floor(Math.abs(ms) / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

const pad2 = (n) => String(n).padStart(2, "0");

const formatHMS = (ms) => {
  const totalSeconds = Math.floor(Math.abs(ms) / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
};

const SlaTimer = ({
  dueAt,
  isUrgent = false,
  status,
  variant = "badge",
  visibleStatuses = CENTER_WAITING_STATUSES,
}) => {
  const isDetail = variant === "detail";
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    // Detail view ticks every second (HH:MM:SS); badge refreshes every 30s.
    const intervalMs = isDetail ? 1000 : 30000;
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [isDetail]);

  if (!dueAt) return null;
  if (
    visibleStatuses &&
    status &&
    !visibleStatuses
      .map((s) => String(s).toUpperCase())
      .includes(String(status).toUpperCase())
  ) {
    return null;
  }

  const dueMs = new Date(dueAt).getTime();
  if (isNaN(dueMs)) return null;

  const remaining = dueMs - now;
  const breached = remaining <= 0;
  const urgent = isUrgent || breached;

  // ===== Detail variant (order-details header) — matches the lab design =====
  if (isDetail) {
    const timeText = formatHMS(remaining);
    return (
      <span
        title={`SLA due: ${tzDateTimeNumeric(new Date(dueAt))}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 20px",
          borderRadius: 12,
          background: "#fdecef",
          fontSize: 18,
          fontWeight: 600,
          lineHeight: 1.2,
          whiteSpace: "nowrap",
          color: "#2b3348",
        }}
      >
        <FaHourglassHalf size={18} color="#2b3348" />
        <span>{breached ? "Overdue by:" : "Time Remaining:"}</span>
        <b style={{ color: "#e11d48", fontWeight: 800, letterSpacing: 0.3 }}>
          {timeText} Hrs
        </b>
      </span>
    );
  }

  // ===== Badge variant (order tables) =====
  const colors = urgent
    ? { bg: "#fef2f2", fg: "#dc2626", border: "#fecaca" }
    : remaining <= 30 * 60000
      ? { bg: "#fffbeb", fg: "#b45309", border: "#fde68a" }
      : { bg: "#f0fdfa", fg: "#0f766e", border: "#99f6e4" };

  const label = breached
    ? `Overdue ${formatRemaining(remaining)}`
    : `${formatRemaining(remaining)} left`;

  return (
    <span
      title={`Action SLA due: ${tzDateTimeNumeric(new Date(dueAt))}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 10px",
        borderRadius: 999,
        background: colors.bg,
        color: colors.fg,
        border: `1px solid ${colors.border}`,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: colors.fg,
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );
};

export default SlaTimer;
